import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  USER_ID,
  ALTERNATE_LOGIN_ID,
  FIRST_NAME,
  LAST_NAME,
  MOBILE_NO,
  LANDLINE_NO,
  EMAIL_ID_1,
  EMAIL_ID_2,
  ADDRESS_1,
  ADDRESS_2,
  ZIPCODE,
  STATE,
  CITY,
  PO_BOX,
  OCCUPANCY_TYPE,
  START_DATE,
  END_DATE,
  ADDRESS,
  USERNAME,
  IS_BOM_USER,
  BOM_POSITION,
  BOM_START_DATE,
  BOM_END_DATE,
  SHOW_CORRESPONDENCE_ADDRESS,
  CORRESPONDENCE_ADDRESS,
} from "./consts";
import { getRequiredFields } from "@/lib/utils";
import { useGetBOMPositions } from "@/services";
import { useEffect, useState } from "react";
import useApis from "./useApis";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { schema } from "./validations";

const useStep2 = () => {
  const form = useForm<any>({
    defaultValues: {
      [USER_ID]: "",
      [ALTERNATE_LOGIN_ID]: "",
      [FIRST_NAME]: "",
      [LAST_NAME]: "",
      [MOBILE_NO]: "",
      [LANDLINE_NO]: "",
      [EMAIL_ID_1]: "",
      [EMAIL_ID_2]: undefined,
      [ADDRESS]: {
        [ADDRESS_1]: "",
        [ADDRESS_2]: undefined,
        [ZIPCODE]: "",
        [STATE]: "",
        [CITY]: "",
        [PO_BOX]: "",
      },
      [OCCUPANCY_TYPE]: "owner",
      [START_DATE]: "",
      [END_DATE]: undefined,
      [USERNAME]: "",
      [IS_BOM_USER]: false,
      [BOM_POSITION]: "",
      [BOM_START_DATE]: "",
    },
    resolver: zodResolver(schema),
  });
  const [stateKey, setStateKey] = useState<number>(Math.random() * 3);

  const _userDetails = useSelector(
    (state: RootState) => state.currentData.userData
  );
  const communityData = useSelector(
    (state: RootState) => state.currentData.communityData
  );
  const id = communityData?.communityId;
  const userId = _userDetails?.userId;

  const {
    userDetails,
    isUserDetailsLoading,
    isPropertyOwnerDetailsLoading,
    propertyOwnerDtl,
    bomUserDetails,
    isUserDetailsRefetching,
    isPropertyOwnerDetailsRefetching,
    isBomUserDetailsRefetching,
  } = useApis();

  // Dynamically determine required fields
  const requiredFields = getRequiredFields(schema);

  const stateList = useSelector(
    (state: RootState) => state.currentData.stateData
  );
  // to get all the positions for the community
  const { data: bomPositions, isLoading: isBOMPositionsLoading } =
    useGetBOMPositions({ communityId: id as string });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (userDetails || propertyOwnerDtl || bomUserDetails) {
      form.reset({
        [USER_ID]: userDetails?.data?.[USER_ID] || "",
        [ALTERNATE_LOGIN_ID]: userDetails?.data?.[ALTERNATE_LOGIN_ID] || "",
        [FIRST_NAME]: userDetails?.data?.[FIRST_NAME] || "",
        [LAST_NAME]: userDetails?.data?.[LAST_NAME] || "",
        [MOBILE_NO]: userDetails?.data?.[MOBILE_NO] || "",
        [LANDLINE_NO]: userDetails?.data?.[LANDLINE_NO] || undefined,
        [EMAIL_ID_1]: userDetails?.data?.[EMAIL_ID_1] || "",
        [EMAIL_ID_2]: userDetails?.data?.[EMAIL_ID_2] || undefined,
        [ADDRESS]: {
          [ADDRESS_1]: userDetails?.data?.[ADDRESS]?.[ADDRESS_1] || "",
          [ADDRESS_2]: userDetails?.data?.[ADDRESS]?.[ADDRESS_2] || undefined,
          [ZIPCODE]: userDetails?.data?.[ADDRESS]?.[ZIPCODE] || "",
          [STATE]:
            stateList?.find(
              (state: { name: string; code: string }) =>
                state.name == userDetails?.data?.[ADDRESS]?.[STATE] ||
                state.code == userDetails?.data?.[ADDRESS]?.[STATE]
            )?.code || "",
          [CITY]: userDetails?.data?.[ADDRESS]?.[CITY] || "",
          [PO_BOX]: userDetails?.data?.[ADDRESS]?.[PO_BOX] || "",
        },
        [SHOW_CORRESPONDENCE_ADDRESS]: !!userDetails?.data?.[CORRESPONDENCE_ADDRESS],
        [CORRESPONDENCE_ADDRESS]: {
          [ADDRESS_1]: userDetails?.data?.[CORRESPONDENCE_ADDRESS]?.[ADDRESS_1] || "",
          [ADDRESS_2]: userDetails?.data?.[CORRESPONDENCE_ADDRESS]?.[ADDRESS_2] || "",
          [ZIPCODE]: userDetails?.data?.[CORRESPONDENCE_ADDRESS]?.[ZIPCODE] || "",
          [STATE]: userDetails?.data?.[CORRESPONDENCE_ADDRESS]?.[STATE] || "",
          [CITY]: userDetails?.data?.[CORRESPONDENCE_ADDRESS]?.[CITY] || "",
          [PO_BOX]: userDetails?.data?.[CORRESPONDENCE_ADDRESS]?.[PO_BOX] || "",
        },
        [OCCUPANCY_TYPE]: propertyOwnerDtl?.data?.[OCCUPANCY_TYPE] || "owner",
        [START_DATE]: propertyOwnerDtl?.data?.[START_DATE] || "",
        [END_DATE]: propertyOwnerDtl?.data?.[END_DATE] || "",
        [USERNAME]: userDetails?.data?.[USERNAME] || "",
        [IS_BOM_USER]: Boolean(bomUserDetails?.data),
        [BOM_POSITION]: bomUserDetails?.data?.[BOM_POSITION]?.toString() || "",
        [BOM_START_DATE]: bomUserDetails?.data?.startDate || "",
        [BOM_END_DATE]: bomUserDetails?.data?.endDate || "",
      });
      setStateKey(Math.random() * 3);
    }
  }, [userDetails, propertyOwnerDtl, bomUserDetails]);

  return {
    form,
    requiredFields,
    userId,
    stateList,
    bomPositions,
    isBOMPositionsLoading,
    isUserDetailsLoading,
    isPropertyOwnerDetailsLoading,
    propertyOwnerDtl,
    stateKey,
    isUserDetailsRefetching,
    isPropertyOwnerDetailsRefetching,
    isBomUserDetailsRefetching,
  };
};

export default useStep2;
